<template>
  <article v-editable="blok" class="article-wrapper">
    <component :is="pageBannerComponent" v-if="pageBanner" :blok="pageBanner" />
    <section class="block block-service-providers">
      <div class="container">
        <div class="row flex-wrap justify-around">
          <div class="column sm-11 md-5">
            <TagsList :show-all="true" :tags="topics" size="lg" />
            <ServiceProvidersMeta
              :email="blok.email"
              :hours="blok.hours"
              :is-eaaa-member="!!blok.eaaa_member"
              :media="media"
              :phone-alternate="blok.phone_alternate"
              :phone="blok.phone"
              :phones="blok.phones"
              :provider="title"
              :states="states"
              :url="blok.url?.url"
            />
            <ArticleDownloads :downloads="downloads" />
          </div>
          <div class="column sm-11 md-7 mobile-order-first tablet-order-first">
            <div class="page-content">
              <header v-if="!pageBanner">
                <NavigationBreadcrumbs />
                <h1 v-if="title">
                  {{ title }}
                </h1>
                <h2
                  v-if="hasArticleIntroduction"
                  class="h3 light whitespace-pre-line"
                >
                  {{ introduction }}
                </h2>
              </header>
              <RichTextRenderer
                v-if="hasRichTextContent"
                :document="richTextContent"
              />
              <ServiceProvidersCallToAction
                :link="blok.url?.url"
                :provider="title"
              />
              <LazyVisibleArticleToolButtons
                :introduction="introduction"
                :title="title"
                hydrate-on-visible
              />
              <NewsletterPopupTrigger />
            </div>
          </div>
        </div>
      </div>
    </section>
    <component
      :is="block.component"
      v-for="block in blocks"
      :key="block._uid"
      :blok="block"
    />
  </article>
</template>

<script setup lang="ts">
import type { ServiceProvidersStoryblok } from '~/types/storyblok'

const props = defineProps<{
  blok: ServiceProvidersStoryblok
}>()
const {
  pageBanner,
  pageBannerComponent,
  blocks,
  introduction,
  title,
  topics,
  states,
  richTextContent,
  hasRichTextContent,
  hasArticleIntroduction,
  downloads,
} = useStoryContent(toRefs(props))
const media = computed(() => props.blok?.logo)
const seoImageUrl = computed(() => {
  const logo = props.blok.logo?.filename
  if (!logo) {
    return ''
  }
  return seoImageMedia(logo)
})
usePageMeta({ ...toRefs(props), seoImageUrl })
</script>

<style lang="less" scoped>
.block-service-providers {
  margin-top: @gutter-lg + @gutter-xl; // 60

  @media @tablet {
    margin-top: @gutter-xl * 2; // 72
  }

  @media @desktop {
    margin-top: @gutter-xxl + @gutter-xl; // 84
  }

  .article-tool-buttons {
    margin: 0;
    padding-top: @gutter-lg;
    border-top: solid @2px @color-border;

    :deep(.button-group) {
      .justify-center();
    }
  }
}

@media print {
  article {
    section.block.block-service-providers {
      margin-top: @gutter-xs;

      .container {
        .row {
          .column:first-child {
            order: -9999;
            margin-bottom: @gutter-lg;
            flex-basis: 100%;
            border: solid @2px @color-border;
            padding: @gutter @gutter-sm;

            > * {
              max-width: none;
            }

            :deep(ul.icon-list) {
              li {
                padding: 0;
              }
            }
          }
        }
      }
    }
  }
}
</style>
